export const INDIA_TZ = 'Asia/Kolkata';
export const INDIA_UTC_OFFSET = '+05:30';

function getIndiaParts(baseDate) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: INDIA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(baseDate);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Current date/time in India (IST). */
export function getNowInIndia() {
  const p = getIndiaParts(new Date());
  return new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${INDIA_UTC_OFFSET}`);
}

export function toIndiaDateInputValue(date) {
  const p = getIndiaParts(date);
  return `${p.year}-${p.month}-${p.day}`;
}

export function toIndiaTimeInputValue(date) {
  const p = getIndiaParts(date);
  return `${p.hour}:${p.minute}`;
}

/** 12-hour picker → 24-hour `HH:mm` (IST). */
export function to24HourTimeString(hour12, minute, period) {
  const h = parseInt(String(hour12), 10);
  if (!h || h < 1 || h > 12) return '';
  const m = String(parseInt(String(minute), 10) || 0).padStart(2, '0');
  if (period !== 'AM' && period !== 'PM') return '';

  let hour24 = h % 12;
  if (period === 'PM') hour24 += 12;

  return `${String(hour24).padStart(2, '0')}:${m}`;
}

/** Date (IST) → { hour: '1'-'12', minute: '00'-'59', period: 'AM'|'PM' }. */
export function fromIndiaTimeTo12HourParts(date) {
  const p = getIndiaParts(date);
  const hour24 = parseInt(p.hour, 10);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour: String(hour12),
    minute: p.minute,
    period,
  };
}

export function is24HourTimeBefore(timeA, timeB) {
  if (!timeA || !timeB) return false;
  return timeA < timeB;
}

/** Build preferred slot from date + time inputs (interpreted as IST). */
export function buildPreferredDateTimeIndia(dateStr, timeStr) {
  return new Date(`${dateStr}T${timeStr}:00${INDIA_UTC_OFFSET}`);
}

export function getEarliestAllowedIndia(bufferHours = 12) {
  const nowIndia = getNowInIndia();
  return new Date(nowIndia.getTime() + bufferHours * 60 * 60 * 1000);
}

/** API payload: ISO string with +05:30 offset (Indian time). */
export function toIndiaApiIsoString(date) {
  const p = getIndiaParts(date);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${INDIA_UTC_OFFSET}`;
}

export function formatIndiaDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    timeZone: INDIA_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatIndiaTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', {
    timeZone: INDIA_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatIndiaDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: INDIA_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
